import { LorebookEntryOpType } from "@next-platform/packages-ts-types";
/**
 * Prefix for the client-generated id of an entry staged for creation but not yet submitted (a staged
 * create has no backend id). The id is reused across later staged edits of the same entry so
 * CREATE+UPDATE merges into one op; it never reaches the backend (the create submits with no id and
 * the backend assigns the real one on approval).
 */
const TEMP_ENTRY_ID_PREFIX = "staged_tmp_";
const RANDOM_ID_BASE = 36;
const RANDOM_ID_SUFFIX_LENGTH = 12;
/** True when `entryId` is a client-only correlation id for a staged create (not persisted on the backend). */
export function isStagedTempEntryId(entryId) {
    return entryId.startsWith(TEMP_ENTRY_ID_PREFIX);
}
/**
 * Generate a fresh temporary entry id for a staged create.
 * Random (not sequential) so ids are not predictable across sessions; uniqueness only needs to
 * hold within one edit session, and the ids never leave the client.
 */
export function nextTempEntryId() {
    if (typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.randomUUID === "function") {
        return `${TEMP_ENTRY_ID_PREFIX}${globalThis.crypto.randomUUID()}`;
    }
    const randomSuffix = Math.random()
        .toString(RANDOM_ID_BASE)
        .slice(2, 2 + RANDOM_ID_SUFFIX_LENGTH);
    return `${TEMP_ENTRY_ID_PREFIX}${Date.now().toString(RANDOM_ID_BASE)}-${randomSuffix}`;
}
/** Order-insensitive keyword comparison, used to decide whether a staged edit really changed them. */
export function areKeywordsEqual(previous, current) {
    const first = previous ?? [];
    const second = current ?? [];
    if (first.length !== second.length)
        return false;
    const sortedFirst = [...first].sort();
    const sortedSecond = [...second].sort();
    return sortedFirst.every((keyword, index) => keyword === sortedSecond[index]);
}
/**
 * Build the update data for a staged entry edit, keeping only fields whose value actually changed.
 * The clients submit the whole form, so without this an untouched name/content would reach
 * moderation and render as an edit. Returns an empty object when nothing changed.
 */
export function buildChangedEntryData(next, previous) {
    const changed = {};
    if (next.name !== previous.name) {
        changed.name = next.name;
    }
    if (!areKeywordsEqual(next.keywords, previous.keywords)) {
        changed.keywords = next.keywords;
    }
    if (next.content !== previous.content) {
        changed.content = next.content;
    }
    return changed;
}
/**
 * Merge an incoming staged entry change into the op currently accumulated for that entry, following
 * the supported merge table:
 *
 *   CREATE + UPDATE -> CREATE (data merged)
 *   CREATE + DELETE -> (op removed)
 *   UPDATE + UPDATE -> UPDATE (data merged)
 *   UPDATE + DELETE -> DELETE
 *
 * `current` is `undefined` when the entry has no accumulated op yet, and `null` once a CREATE+DELETE
 * has cancelled the entry out (so a later change to the same id is ignored rather than resurrecting a
 * deleted-before-submit entry). Returns the new accumulated op (or `null` when cancelled out).
 */
function mergeEntryChange(current, change) {
    // A create for a brand-new entry id.
    if (change.kind === "create") {
        return { entry_id: change.entryId, op: LorebookEntryOpType.CREATE, data: change.data };
    }
    if (change.kind === "update") {
        // `null` = cancelled by an earlier CREATE+DELETE; stay cancelled, don't resurrect a temp id.
        if (current === null) {
            return null;
        }
        // First op for this entry: a plain update of an existing entry.
        if (!current) {
            return { entry_id: change.entryId, op: LorebookEntryOpType.UPDATE, data: change.data };
        }
        // CREATE + UPDATE -> CREATE with the update folded in; UPDATE + UPDATE -> merged UPDATE.
        if (current.op === LorebookEntryOpType.CREATE || current.op === LorebookEntryOpType.UPDATE) {
            return { ...current, data: { ...current.data, ...change.data } };
        }
        // DELETE + UPDATE is not a supported sequence; keep the delete.
        return current;
    }
    // change.kind === "delete"
    // CREATE + DELETE -> remove the op entirely (never reaches the backend). `null` means an earlier
    // CREATE+DELETE already cancelled the id, so it stays cancelled rather than emitting a DELETE for
    // a temp id the server never saw.
    if (current === null || current?.op === LorebookEntryOpType.CREATE) {
        return null;
    }
    // UPDATE + DELETE (or first op) -> DELETE.
    return { entry_id: change.entryId, op: LorebookEntryOpType.DELETE };
}
/**
 * Aggregate a session of staged parent + entry edits into a single `PendingLorebookData` moderation
 * snapshot.
 *
 * Guarantees:
 * - At most one `entry_op` per entry id (duplicates merged via {@link mergeEntryChange}).
 * - CREATE+DELETE entries are dropped entirely.
 * - `update` ops with empty `data` are dropped (the backend rejects `data: {}`).
 * - `entry_ops` is omitted entirely when no ops survive (the backend requires `>= 1` when present).
 * - Parent `name` / `description` / `avatar_url` / `tags` are only included when they were staged.
 * - Op order follows the first time each entry was touched.
 */
export function aggregateStagedEdits(edits) {
    // Preserve first-touch order while allowing in-place op replacement.
    const order = [];
    const opsByEntryId = new Map();
    for (const change of edits.entryChanges) {
        if (!opsByEntryId.has(change.entryId)) {
            order.push(change.entryId);
        }
        opsByEntryId.set(change.entryId, mergeEntryChange(opsByEntryId.get(change.entryId), change));
    }
    const entry_ops = order
        .map((entryId) => opsByEntryId.get(entryId))
        .filter((op) => op != null)
        // A merged update can collapse to no changed fields; the backend rejects empty-data updates.
        .filter((op) => op.op !== LorebookEntryOpType.UPDATE || Object.keys(op.data ?? {}).length > 0);
    const snapshot = {};
    // The backend rejects `entry_ops: []`; only include the field when non-empty.
    if (entry_ops.length > 0) {
        snapshot.entry_ops = entry_ops;
    }
    if (edits.parent.name !== undefined) {
        snapshot.name = edits.parent.name;
    }
    if (edits.parent.description !== undefined) {
        snapshot.description = edits.parent.description;
    }
    if (edits.parent.avatar_url !== undefined) {
        snapshot.avatar_url = edits.parent.avatar_url;
    }
    if (edits.parent.tags !== undefined) {
        snapshot.tags = edits.parent.tags;
    }
    return snapshot;
}
